import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Loader2, Lock, ShieldCheck,User } from 'lucide-react';
import { setUser } from '../../redux/authSlice'; // Adjust path if needed

export default function ProfileSettings({ darkMode }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.currentUser);
  
  const borderColor = darkMode ? 'border-gray-800/50' : 'border-gray-200';
  const cardBg = darkMode ? 'bg-[#161922]' : 'bg-white';
  const inputBg = darkMode ? 'bg-[#0B1220]' : 'bg-gray-50';

  // Password Form State
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const isSSOUser = Boolean(user?.is_sso);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!isSSOUser && !oldPassword) return toast.error("Please enter your current password.");
    if (newPassword.length < 6) return toast.error("New password must be at least 6 characters.");
    if (newPassword !== confirmPassword) return toast.error("New passwords do not match.");

    setLoading(true);
    try {
      const res = await axios.post("https://api.testoraglobal.in/api/user/update-password", {
        email: user.email,
        old_password: oldPassword,
        new_password: newPassword,
        is_sso: isSSOUser
      });

      if (res.data.success) {
        toast.success(res.data.message);
        
        // Clear form
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        // Update Redux state so UI stops showing "Set Password" next time
        dispatch(setUser({ ...user, is_sso: false, password: newPassword }));
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update password.");
    } finally {
      setLoading(false);
    }
  };

  if (!user) return <p>Loading profile...</p>;

  // Split name for UI (Fallback logic)
  const nameParts = user.user_name ? user.user_name.split(' ') : ['User', ''];
  const firstName = nameParts[0];
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
  const initials = firstName.charAt(0) + (lastName ? lastName.charAt(0) : '');

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
      
      {/* 1. PROFILE DETAILS (READ-ONLY) */}
      <div className={`p-8 rounded-3xl border ${cardBg} ${borderColor} shadow-lg`}>
        <div className="flex justify-between items-start mb-8">
          <h3 className="text-xl font-bold flex items-center gap-2"><User size={20} className="text-purple-500"/> Personal Information</h3>
          {isSSOUser && <span className="text-xs bg-blue-500/10 text-blue-400 font-bold px-3 py-1 rounded-full border border-blue-500/20 uppercase tracking-wide flex items-center gap-1"><ShieldCheck size={12}/> SSO Account</span>}
        </div>
        
        <div className="flex items-center gap-6 mb-10">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-2xl font-bold text-white shadow-xl">
            {initials.toUpperCase()}
          </div>
          <div>
            <h4 className="text-xl font-bold">{user.user_name}</h4>
            {/* <p className="text-sm text-gray-500">ID: {user.user_id} • Org ID: {user.org_id}</p> */}
          </div>
        </div>

        <div className="space-y-6 pointer-events-none opacity-80">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-[11px] font-bold text-gray-500 uppercase mb-2">First Name</label>
              <input type="text" value={firstName} readOnly className={`w-full p-3 rounded-xl border ${borderColor} ${inputBg} text-sm text-gray-300`} />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-gray-500 uppercase mb-2">Last Name</label>
              <input type="text" value={lastName} readOnly className={`w-full p-3 rounded-xl border ${borderColor} ${inputBg} text-sm text-gray-300`} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-[11px] font-bold text-gray-500 uppercase mb-2">Email Address</label>
              <input type="email" value={user.email} readOnly className={`w-full p-3 rounded-xl border ${borderColor} ${inputBg} text-sm text-gray-300`} />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-gray-500 uppercase mb-2">System Role</label>
              <input type="text" value={user.role.replace('_', ' ').toUpperCase()} readOnly className={`w-full p-3 rounded-xl border ${borderColor} ${inputBg} text-sm text-gray-300`} />
            </div>
          </div>
        </div>
      </div>

      {/* 2. PASSWORD MANAGEMENT */}
      <div className={`p-8 rounded-3xl border ${cardBg} ${borderColor} shadow-lg`}>
        <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
          <Lock size={20} className={isSSOUser ? "text-blue-500" : "text-green-500"}/> 
          {isSSOUser ? "Set Account Password" : "Change Password"}
        </h3>
        <p className="text-sm text-gray-400 mb-8">
          {isSSOUser 
            ? "You registered via Google. You can set a manual password here if you ever want to log in without Google." 
            : "Update your password to keep your account secure."}
        </p>

        <form onSubmit={handlePasswordChange} className="space-y-6 max-w-lg">
          {!isSSOUser && (
            <div>
              <label className="block text-[11px] font-bold text-gray-500 uppercase mb-2">Current Password</label>
              <input 
                type="password" 
                value={oldPassword} 
                onChange={(e) => setOldPassword(e.target.value)} 
                disabled={loading}
                placeholder="••••••••"
                className={`w-full p-3 rounded-xl border ${borderColor} ${inputBg} outline-none focus:border-purple-500 transition-all text-sm`} 
              />
            </div>
          )}

          <div>
            <label className="block text-[11px] font-bold text-gray-500 uppercase mb-2">New Password</label>
            <input 
              type="password" 
              value={newPassword} 
              onChange={(e) => setNewPassword(e.target.value)} 
              disabled={loading}
              placeholder="••••••••"
              className={`w-full p-3 rounded-xl border ${borderColor} ${inputBg} outline-none focus:border-purple-500 transition-all text-sm`} 
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-gray-500 uppercase mb-2">Confirm New Password</label>
            <input 
              type="password" 
              value={confirmPassword} 
              onChange={(e) => setConfirmPassword(e.target.value)} 
              disabled={loading}
              placeholder="••••••••"
              className={`w-full p-3 rounded-xl border ${borderColor} ${inputBg} outline-none focus:border-purple-500 transition-all text-sm`} 
            />
          </div>

          <div className="pt-2">
            <button 
              type="submit" 
              disabled={loading}
              className="px-6 py-3 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold transition-all shadow-lg shadow-purple-500/20 flex items-center justify-center gap-2 w-full sm:w-auto disabled:opacity-50"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {isSSOUser ? "Save Password" : "Update Password"}
            </button>
          </div>
        </form>
      </div>

    </div>
  );
}